import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

import { courseRoutes } from 'src/modules/course-creator';
import { pageCreatorRoutes } from 'src/modules/page-creator';
import { muiBuilderRoutes } from 'src/modules/mui-builder/routes';
import { formsRoutes } from 'src/modules/forms/routes';
import { reportsRoutes } from 'src/modules/reports/routes';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/one'));
const PageTwo = lazy(() => import('src/pages/dashboard/two'));
const PageThree = lazy(() => import('src/pages/dashboard/three'));
const PageFour = lazy(() => import('src/pages/dashboard/four'));
const PageFive = lazy(() => import('src/pages/dashboard/five'));
const PageSix = lazy(() => import('src/pages/dashboard/six'));
const ThemeSettingsPage = lazy(() => import('src/pages/dashboard/theme-settings'));
const ShopListPage = lazy(() => import('src/pages/dashboard/shops/ShopListPage'));
const ShopCreatePage = lazy(() => import('src/pages/dashboard/shops/ShopCreatePage'));
const ShopEditPage = lazy(() => import('src/pages/dashboard/shops/ShopEditPage'));
const ShopManagerPage = lazy(() => import('src/pages/dashboard/shops/ShopManagerPage'));
const ReferalsListView = lazy(() => import('src/pages/dashboard/referals/list'));
const CaseStudiesPage = lazy(() => import('src/pages/dashboard/CaseStudiesPage'));
const AffiliateAdminPage = lazy(() => import('src/pages/dashboard/affiliate-admin'));
const PaymentTariffPage = lazy(() => import('src/pages/dashboard/payment/payment-tariff-page'));
const ChatPage = lazy(() => import('src/pages/dashboard/chat/ChatPage'));
const EmptyShopSelection = lazy(() => import('src/modules/course-creator/v2/components/EmptyShopSelection'));
const FormsEmptyShopSelection = lazy(() => import('src/modules/forms/components/EmptyShopSelection'));
const ChannelsPage = lazy(() => import('src/pages/dashboard/channels/ListPage'));
const ChannelsSelectionView = lazy(() => import('src/sections/dashboard/channels/view/ChannelsSelectionView'));
const ChannelsShopGuard = lazy(() => import('src/sections/dashboard/channels/view/ChannelsShopGuard'));


// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard/:shopid',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'two', element: <PageTwo /> },
      { path: 'three', element: <PageThree /> },
      { path: 'theme', element: <ThemeSettingsPage /> },
      {
        path: 'group',
        children: [
          { element: <PageFour />, index: true },
          { path: 'five', element: <PageFive /> },
          { path: 'six', element: <PageSix /> },
        ],
      },
      courseRoutes,
      pageCreatorRoutes,
      muiBuilderRoutes,
      formsRoutes,
      reportsRoutes,
      {
        path: 'channels',
        element: <ChannelsShopGuard />,
        children: [
          { element: <ChannelsPage />, index: true },
        ]
      },
    ],
  },
  {
    path: 'dashboard/shops',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <ShopListPage />, index: true },
      { path: 'new', element: <ShopCreatePage /> },
      { path: ':id/edit', element: <ShopEditPage /> },
      { path: ':id/manager', element: <ShopManagerPage /> },
      { path: ':sid/payment', element: <PaymentTariffPage /> },
      { path: ':id/chat', element: <ChatPage /> },
      { path: ':id/chat/:conversationKey', element: <ChatPage /> },
      { path: ':id/chat/new', element: <ChatPage /> },
    ]
  },
  {
    path: 'dashboard/referrals',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <ReferalsListView />, index: true },
    ]
  },
  {
    path: 'dashboard/affiliate',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { path: 'admin', element: <AffiliateAdminPage /> },
    ]
  },
  {
    path: 'dashboard/cases',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      { element: <CaseStudiesPage />, index: true },
    ]
  },
  {
    path: 'dashboard/courses-selection',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <EmptyShopSelection />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: 'dashboard/forms-selection',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <FormsEmptyShopSelection />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: 'dashboard/reports-selection',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <FormsEmptyShopSelection />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
  },
  {
    path: 'dashboard/channels-selection',
    element: (
      <AuthGuard userType="user">
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <ChannelsSelectionView />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
  }
];
